import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navRoutes, navCategories } from '@/mocks/nav';
import { appBrand } from '@/mocks/user';
import { useAppSelector } from '@/store/hooks';
import UserMenu from './UserMenu';
import NotificationsMenu from './NotificationsMenu';
import { Icon } from '@/components/common';
import logoColor from '@/assets/images/midani-logo-color.png';
import logoWhite from '@/assets/images/midani-logo-white.png';

interface HeaderProps {
  /** Sidebar collapsed → brand shows the logo only, sized to the rail. */
  collapsed?: boolean;
}

const Header = ({ collapsed = false }: HeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const mode = useAppSelector((state) => state.theme.mode);
  // Brand strip sits on the header's white surface — color logo in light, white in dark.
  const logo = mode === 'dark' ? logoWhite : logoColor;

  const activeItem = navRoutes.find((item) => location.pathname.startsWith(item.path));
  // Parent crumb = the active item's category header (falls back to nothing for
  // standalone items like Overview that have no category).
  const activeCategory = navCategories.find((c) => c.key === activeItem?.category);

  return (
    <header className='app-header'>
      {/* Left: brand, nav arrows + breadcrumb */}
      <div className='app-header__left'>
        <div className={`app-header__brand${collapsed ? ' app-header__brand--collapsed' : ''}`}>
          <span className='app-header__logo'>
            <img src={logo} alt='' className='app-header__logo-img' />
          </span>
          {!collapsed && <span className='app-header__brand-name'>{appBrand.name}</span>}
        </div>

        <div className='app-header__nav-arrows'>
          <button
            type='button'
            className='app-header__icon-btn'
            onClick={() => navigate(-1)}
            aria-label={t('header_back')}
          >
            <span className='app-header__chevron app-header__chevron--start' />
          </button>
          <button
            type='button'
            className='app-header__icon-btn'
            onClick={() => navigate(1)}
            aria-label={t('header_forward')}
          >
            <span className='app-header__chevron app-header__chevron--end' />
          </button>
        </div>

        <nav className='app-header__breadcrumb' aria-label='Breadcrumb'>
          {activeCategory && (
            <span className='app-header__crumb'>{t(activeCategory.labelKey)}</span>
          )}
          {activeItem && (
            <>
              {activeCategory && <span className='app-header__crumb-sep'>/</span>}
              <span className='app-header__crumb app-header__crumb--current'>
                {t(activeItem.labelKey)}
              </span>
            </>
          )}
        </nav>
      </div>

      {/* Right: notifications, help, user */}
      <div className='app-header__right'>
        <NotificationsMenu />

        <button
          type='button'
          className='app-header__icon-btn'
          aria-label={t('header_help')}
          title={t('header_help')}
        >
          <Icon name='circle-question-mark' size={18} />
        </button>

        <UserMenu />
      </div>
    </header>
  );
};

export default Header;
